import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineryConductorService } from '../../../services/machineryConductorService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';

const Conductors = () => {
  const [conductors, setConductors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [conductorToDelete, setConductorToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    loadConductors();
  }, []);

  const loadConductors = async () => {
    setLoading(true);
    try {
      const response = await machineryConductorService.getConductors();
      if (response.success) {
        setConductors(response.data);
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar los conductores', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (conductor) => {
    setConductorToDelete(conductor);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!conductorToDelete) return;
    try {
      const response = await machineryConductorService.deleteConductor(conductorToDelete.id);
      if (response.success) {
        setToast({ isOpen: true, message: 'Conductor eliminado correctamente', type: 'success' });
        loadConductors();
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al eliminar', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: error.response?.data?.message || 'Error al eliminar el conductor', type: 'error' });
    } finally {
      setIsDeleteModalOpen(false);
      setConductorToDelete(null);
    }
  };

  const filteredConductors = conductors.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.document?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.license?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const isActive = status === 'active';
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800';
  };
  const getStatusLabel = (status) => (status === 'active' ? 'Activo' : 'Inactivo');

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Conductores</h1>
          <p className="text-gray-600 mt-1">Configure los conductores u operadores para eventos y vehículos</p>
        </div>
        <Link
          to="/machinery/conductors/create"
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          + Nuevo Conductor
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Nombre, documento o licencia..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
          <p className="mt-2 text-gray-600">Cargando conductores...</p>
        </div>
      ) : filteredConductors.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No se encontraron conductores</p>
          <Link to="/machinery/conductors/create" className="mt-4 inline-block text-teal-600 hover:text-teal-700 font-medium">+ Agregar conductor</Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Documento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Licencia</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConductors.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{c.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.document || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.license || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(c.status)}`}>
                        {getStatusLabel(c.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link to={`/machinery/conductors/${c.id}/edit`} className="text-teal-600 hover:text-teal-900 mr-4">Editar</Link>
                      <button onClick={() => handleDelete(c)} className="text-red-600 hover:text-red-900">Eliminar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setConductorToDelete(null); }}
        onConfirm={confirmDelete}
        title="Eliminar conductor"
        message={`¿Está seguro de eliminar al conductor ${conductorToDelete?.name}?`}
      />
      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </div>
  );
};

export default Conductors;
